import { Button, Grid, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  getUsersWithFilters,
  ProcureUser,
  createUserInvitations as createUserInvitationsMutation,
  getProperties,
  CONTACT_METHOD_TYPE,
  USER_ROLES,
  getUserInvitations,
  MIXPANEL_EVENTS,
  Property,
} from 'vendor-marketplace-shared-library';
import UserFormModal from '../../components/user/UserFormModal';
import useAlert from '../../hooks/useAlert';
import { CREATE_USER_INVITATIONS } from '../../error-messages';
import UserInvitationsModule from './components/UserInvitationsModule';
import { formatPhoneNumber } from '../../utilities/formatters';
import useAuth from '../../hooks/useAuth';
import useMixpanel from '../../hooks/useMixpanel';

const columnWidth = (window.innerWidth * 0.75) / 4;
const columnWidthOnMobile = 175;
const calculatedColumnWidth = window.innerWidth > 700 ? columnWidth : columnWidthOnMobile;

const columns: GridColDef[] = [
  { field: 'fullName', headerName: 'Name', width: calculatedColumnWidth },
  { field: 'role', headerName: 'Role', width: calculatedColumnWidth },
  { field: 'phone', headerName: 'Phone', width: calculatedColumnWidth },
  { field: 'email', headerName: 'Email', width: calculatedColumnWidth },
];

export type InviteDataInput = {
  firstName: string;
  lastName: string;
  contactMethodType: string;
  contactMethodValue: string;
  userRoleId: number;
  propertyIds: string[];
};

export default function UsersPage() {
  const { userQuery } = useAuth();
  const navigate = useNavigate();
  const alert = useAlert();
  const { mixpanelTrack } = useMixpanel();
  const [openFormModal, setOpenFormModal] = useState(false);
  const [inviteData, setInviteData] = useState<InviteDataInput>({
    firstName: '',
    lastName: '',
    contactMethodType: CONTACT_METHOD_TYPE.PHONE,
    contactMethodValue: '',
    userRoleId: USER_ROLES.ADMIN.id,
    propertyIds: [],
  });

  const { data: usersData } = useQuery(getUsersWithFilters);
  const users = usersData?.getUsersWithFilters || [];
  const { data: propertiesData } = useQuery(getProperties);
  const properties = propertiesData?.getProperties || [];

  const { data: userInvitationsData, refetch: refetchUserInvitations } = useQuery(
    getUserInvitations,
    {
      variables: { isPendingOnly: true },
    },
  );

  const resetForm = () => {
    setInviteData({
      firstName: '',
      lastName: '',
      contactMethodType: CONTACT_METHOD_TYPE.PHONE,
      contactMethodValue: '',
      userRoleId: USER_ROLES.ADMIN.id,
      propertyIds: [],
    });
  };

  const [createUserInvitations] = useMutation(createUserInvitationsMutation, {
    refetchQueries: [getUsersWithFilters],
    onError: () => {
      alert.setAlert({ severity: 'error', message: CREATE_USER_INVITATIONS });
    },
    onCompleted: data => {
      if (data.createUserInvitations) {
        setOpenFormModal(false);
        resetForm();
      }
      refetchUserInvitations();
    },
  });

  const sendInvite = async () => {
    await createUserInvitations({
      variables: {
        input: {
          firstName: inviteData.firstName,
          lastName: inviteData.lastName,
          contactMethodType: inviteData.contactMethodType,
          contactMethodValue: inviteData.contactMethodValue,
          userRoleId: Number(inviteData.userRoleId),
          propertyIds:
            inviteData.userRoleId === USER_ROLES.ADMIN.id
              ? properties.map((property: Property) => Number(property.id))
              : inviteData.propertyIds.map(id => Number(id)),
        },
      },
    });
  };

  const rows = useMemo(() => {
    return users.map((user: ProcureUser) => ({
      ...user,
      id: user.id,
      role: user.userRole?.name || '',
      phone: formatPhoneNumber(user.phoneContactMethod?.value || '') || '—',
      email: user.emailContactMethod?.value || '—',
    }));
  }, [users]);

  const onRowClickNavigation = (id: string) => {
    mixpanelTrack(MIXPANEL_EVENTS.ORGANIZATION_SETTINGS_USER_ROW_PRESSED, { id });
    if (userQuery?.data.me.id === id) {
      navigate('/settings');
    } else {
      navigate(`/users/${id}`);
    }
  };

  return (
    <Grid container spacing={1} rowGap={2}>
      <Grid item container sx={styles.subContainer}>
        <Grid item>
          <Typography variant={'h5'}>Users in your organization</Typography>
        </Grid>
        <Grid item>
          <Button
            onClick={() => {
              mixpanelTrack(MIXPANEL_EVENTS.ORGANIZATION_SETTINGS_CREATE_USER_PRESSED);
              mixpanelTrack(MIXPANEL_EVENTS.INVITATIONS_INVITE_MANUALLY_PRESSED);
              setOpenFormModal(true);
            }}
          >
            Invite a user
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={12} sx={styles.tableContainer}>
        <Grid container sx={styles.nowrap}>
          <Grid item xs={12}>
            <DataGrid
              sx={styles.dataGrid}
              rows={rows}
              columns={columns}
              onRowClick={row => onRowClickNavigation(row.row.id)}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                    page: 0,
                  },
                },
                sorting: {
                  sortModel: [{ field: 'name', sort: 'asc' }],
                },
              }}
              pageSizeOptions={[5, 10]}
            />
          </Grid>
        </Grid>
      </Grid>
      {userInvitationsData?.getUserInvitations.length > 0 && (
        <Grid item xs={12} sx={styles.inviteContainer}>
          <UserInvitationsModule
            userInvitations={userInvitationsData?.getUserInvitations ?? []}
            refetchInvites={refetchUserInvitations}
          />
        </Grid>
      )}
      {openFormModal && (
        <UserFormModal
          open={openFormModal}
          setOpen={setOpenFormModal}
          inviteData={inviteData}
          setInviteData={setInviteData}
          properties={properties}
          onSubmit={sendInvite}
          resetForm={resetForm}
        />
      )}
    </Grid>
  );
}

const styles = {
  subContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dataGrid: {
    minHeight: 150,
    '& .MuiDataGrid-cell:focus': {
      outline: ' none',
    },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
    minWidth: '525px',
    // Hide the scrollbars
    '& .MuiDataGrid-virtualScroller': {
      overflow: 'hidden !important',
    },
    '& .MuiDataGrid-scrollbar--horizontal': {
      display: 'none',
    },
  },
  tableContainer: {
    overflowX: 'auto',
    maxWidth: { xs: '310px', sm: '100%' },
  },
  nowrap: {
    flexWrap: 'nowrap',
  },
  inviteContainer: {
    maxWidth: { xs: '310px', sm: '100%' },
  },
};
